/**
 * TranscriptOverlay Component
 * 
 * Displays conversation messages and hints in chronological order with smart scroll
 * - Allows manual scrolling through message history
 * - Auto-scrolls to new messages only when user is already at bottom
 * 
 * REFACTORED (2026-01): Handles visual-only speaking state.
 * 
 * When isUserSpeaking=true but no message exists yet (before SERVER_SPEECH_STARTED),
 * we show a visual-only waveform bubble. This provides instant feedback while waiting
 * for the server to confirm speech and create the actual message.
 */

import { MutableRefObject, useRef, useState, useCallback, useEffect } from 'react';
import MessageBubble from './MessageBubble';
import HintBubble from './HintBubble';
import PendingMessageBubble from './PendingMessageBubble';
import AIResponseBubble from './AIResponseBubble';
import ProcessingBubble from './ProcessingBubble';
import PTTButton from './PTTButton';
import AudioWaveform from '../common/AudioWaveform';
import { MultilingualCharacterName } from '../utils/characterName';

interface Message {
  speaker: 'user' | 'ai';
  text: string;
  timestamp: number;
}

interface Hint {
  id?: string;
  text: string;
  timestamp: number;
  type: 'suggestion' | 'warning' | 'praise';
  followed?: boolean;
  autoResolved?: boolean; // Hint was resolved without being opened
}

interface Message {
  speaker: 'user' | 'ai';
  text: string;
  timestamp: number;
  id?: string; // For matching temp message with final
  audioBlob?: Blob; // 🔊 Recorded audio for debugging
}

interface TranscriptOverlayProps {
  messages: Message[];
  hints: Hint[];
  showPendingBubble: boolean;
  transcriptInProgress?: string;
  aiResponseInProgress?: string;
  avatarImageUrl?: string;
  characterName?: string | MultilingualCharacterName;
  scrollToBottom: MutableRefObject<boolean>;
  // Audio visualization (user only - AI waveform stays in header)
  getMicrophoneFrequencyData?: () => Uint8Array;
  isUserSpeaking?: boolean;
  currentTranscriptId?: string | null; // ID of message currently being spoken
  // ⌨️ Keyboard mode
  isKeyboardMode?: boolean;
  // 🎙️ PTT mode
  isPTTMode?: boolean;
  isWaitingForTranscript?: boolean;
  onPTTPressed?: () => void;
  onPTTReleased?: () => void;
  // 🎤 Microphone control for audio playback
  onPauseMicrophone?: () => void;
  onResumeMicrophone?: () => void;
  // 🔊 Audio playback state (for PTT button - stays "responding" until audio finishes)
  isAudioPlaying?: boolean;
  // 🔬 Analysis Mode processing indicator
  isAnalysisModeProcessing?: boolean;
  // ⏸️ Session paused - hide floating PTT so it doesn't overlap pause screen
  isPaused?: boolean;
  // Hide the dark radial gradient behind PTT (e.g. when Info modal is open over white)
  hidePTTGradient?: boolean;
  // 🎙️ PTT readiness — false during greeting (mic + setPTTMode not yet armed).
  // When false, button is shown disabled and touches/clicks are ignored to prevent
  // the "first press is silently dropped" bug.
  isPTTReady?: boolean;
}

export default function TranscriptOverlay({
  messages,
  hints,
  showPendingBubble,
  transcriptInProgress,
  aiResponseInProgress,
  avatarImageUrl,
  characterName,
  scrollToBottom,
  getMicrophoneFrequencyData,
  isUserSpeaking = false,
  currentTranscriptId = null,
  isKeyboardMode = false,
  isPTTMode = false,
  isWaitingForTranscript = false,
  onPTTPressed,
  onPTTReleased,
  onPauseMicrophone,
  onResumeMicrophone,
  isAudioPlaying = false,
  isAnalysisModeProcessing = false,
  isPaused = false,
  hidePTTGradient = false,
  isPTTReady = true,
}: TranscriptOverlayProps) {
  // 🚨 CRITICAL: All hooks must be called BEFORE any early returns
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showNewMessageButton, setShowNewMessageButton] = useState(false);
  const previousMessageCountRef = useRef(0);

  // Track if PTT has been used to adjust layout (move button down, hide text)
  const [hasUsedPTT, setHasUsedPTT] = useState(false);

  useEffect(() => {
    // If the user starts speaking, or if there's already a message from the user, they know how to use PTT
    if (isUserSpeaking || messages.some(msg => msg.speaker === 'user')) {
      setHasUsedPTT(true);
    }
  }, [isUserSpeaking, messages]);

  // ─── Draggable PTT button hooks (must be before early return) ───
  // DESIGN: Two phases after touch start:
  //   Phase 1 (first ~180ms): Undecided. If finger moves → drag-only. If timer fires → PTT.
  //   Phase 2 (after PTT activates): PTT is active. Moving finger drags AND keeps PTT alive.
  //   Touch end always releases PTT if it was active, and stops drag.
  //
  // IMPORTANT: All touch handlers use refs for callbacks and state, NOT closure values.
  // This prevents React re-renders (e.g. when isUserSpeaking changes) from replacing
  // the handler functions mid-gesture, which could cause the browser to drop the touch.
  const [pttPosition, setPttPosition] = useState<{ x: number; y: number; isDefault: boolean } | null>(null);
  const pttPositionRef = useRef(pttPosition);
  pttPositionRef.current = pttPosition;

  const onPTTPressedRef = useRef(onPTTPressed);
  onPTTPressedRef.current = onPTTPressed;
  const onPTTReleasedRef = useRef(onPTTReleased);
  onPTTReleasedRef.current = onPTTReleased;
  const pttContainerRef = useRef<HTMLDivElement>(null);
  const isAnalysisModeProcessingRef = useRef(isAnalysisModeProcessing);
  isAnalysisModeProcessingRef.current = isAnalysisModeProcessing;
  const isPTTReadyRef = useRef(isPTTReady);
  isPTTReadyRef.current = isPTTReady;

  const pttDragRef = useRef<{
    startTouchX: number;
    startTouchY: number;
    startPosX: number;
    startPosY: number;
    pttActive: boolean;
    dragOnly: boolean;
  } | null>(null);
  const pttDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize default position (center-bottom) on mount
  useEffect(() => {
    if (isPTTMode && typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        if (pttPositionRef.current === null) {
          // We use isDefault: true to signify the default un-dragged state.
          setPttPosition({
            x: 0, 
            y: 0,
            isDefault: true
          });
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isPTTMode]);

  const DRAG_THRESHOLD = 30; // px — generous to avoid finger jitter
  const PTT_DELAY_MS = 150;  // ms — wait before activating PTT; gives time to start dragging

  // Stable handlers — never change identity, read everything from refs.
  //
  // Strategy:
  //   touchStart: start a 150ms timer. Do NOT activate PTT yet.
  //   touchMove (>30px before timer): cancel timer → drag-only, PTT never fires.
  //   timer fires (finger stayed still): activate PTT. Further moves still drag.
  //   touchEnd: if PTT was activated, release it. Clear any pending timer.
  const handlePttTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    // 🔬 Block PTT during Analysis Mode — mic is paused and session is in analysis state
    if (isAnalysisModeProcessingRef.current) return;
    // 🎙️ Block PTT before mic + setPTTMode(true) are armed (during greeting).
    // Otherwise the press is registered here but silently dropped by handlePTTPressed
    // because isPTTModeActive() returns false → "first press never works" on mobile.
    if (!isPTTReadyRef.current) return;
    const touch = e.touches[0];
    const currentPos = pttPositionRef.current ?? { x: 0, y: 0, isDefault: true };

    // Calculate the actual visual position on screen when touch starts
    let effectiveStartX: number;
    let effectiveStartY: number;
    if (currentPos.isDefault && pttContainerRef.current) {
      const rect = pttContainerRef.current.getBoundingClientRect();
      effectiveStartX = rect.left;
      effectiveStartY = rect.top;
    } else {
      effectiveStartX = currentPos.x;
      effectiveStartY = currentPos.y;
    }

    pttDragRef.current = {
      startTouchX: touch.clientX,
      startTouchY: touch.clientY,
      startPosX: effectiveStartX,
      startPosY: effectiveStartY,
      pttActive: false,
      dragOnly: false,
    };
    // Delayed PTT activation
    pttDelayTimerRef.current = setTimeout(() => {
      if (pttDragRef.current && !pttDragRef.current.dragOnly) {
        pttDragRef.current.pttActive = true;
        onPTTPressedRef.current?.();
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(15);
        }
      }
    }, PTT_DELAY_MS);
  }, []); // stable — no deps

  const handlePttTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pttDragRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const dx = touch.clientX - pttDragRef.current.startTouchX;
    const dy = touch.clientY - pttDragRef.current.startTouchY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < DRAG_THRESHOLD) return;

    // First time crossing threshold before PTT activated → drag-only
    if (!pttDragRef.current.pttActive && !pttDragRef.current.dragOnly) {
      pttDragRef.current.dragOnly = true;
      if (pttDelayTimerRef.current) {
        clearTimeout(pttDelayTimerRef.current);
        pttDelayTimerRef.current = null;
      }
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(10);
      }
    }

    // Drag the button (works in both drag-only and PTT+drag modes)
    const containerWidth = pttContainerRef.current?.offsetWidth ?? 160;
    const containerHeight = pttContainerRef.current?.offsetHeight ?? 160;

    // If it was in layout-centered mode, recalibrate start position RIGHT NOW to prevent jumping.
    // The container's width might have changed since touchStart (e.g. "Hold to talk" -> "Listening...")
    if (pttPositionRef.current?.isDefault && pttContainerRef.current) {
      const rect = pttContainerRef.current.getBoundingClientRect();
      pttDragRef.current.startPosX = rect.left - dx;
      pttDragRef.current.startPosY = rect.top - dy;
      setPttPosition({ x: rect.left, y: rect.top, isDefault: false });
      return; // Skip setting this frame, next move will apply delta cleanly
    }

    let newX = pttDragRef.current.startPosX + dx;
    let newY = pttDragRef.current.startPosY + dy;

    newX = Math.max(0, Math.min(window.innerWidth - containerWidth, newX));
    newY = Math.max(60, Math.min(window.innerHeight - containerHeight, newY));

    setPttPosition({ x: newX, y: newY, isDefault: false });
  }, []); // stable — no deps

  const handlePttTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    // Clear pending delay timer
    if (pttDelayTimerRef.current) {
      clearTimeout(pttDelayTimerRef.current);
      pttDelayTimerRef.current = null;
    }
    // Release PTT if it was activated
    if (pttDragRef.current?.pttActive) {
      onPTTReleasedRef.current?.();
    }
    pttDragRef.current = null;
  }, []); // stable — no deps

  // ─── Mouse handlers for desktop (simple click/hold, no drag) ───
  const handlePttMouseDown = useCallback(() => {
    if (isAnalysisModeProcessingRef.current) return;
    if (!isPTTReadyRef.current) return;
    onPTTPressedRef.current?.();
  }, []); // stable — no deps

  const handlePttMouseUp = useCallback(() => {
    onPTTReleasedRef.current?.();
  }, []); // stable — no deps

  const handlePttMouseLeave = useCallback(() => {
    onPTTReleasedRef.current?.();
  }, []); // stable — no deps

  // Also show content when user is speaking (visual-only waveform before message is created)
  const hasContent = messages.length > 0 || transcriptInProgress || aiResponseInProgress || showPendingBubble || isUserSpeaking;

  // Check if user is near bottom (within 150px)
  // With flex-col-reverse, scrollTop near 0 means at bottom (visually)
  const checkIfNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) return true;
    const { scrollTop } = scrollContainerRef.current;
    // With flex-col-reverse, scrollTop is 0 or near 0 when at visual bottom
    const isNear = Math.abs(scrollTop) < 150;
    setIsNearBottom(isNear);
    return isNear;
  }, []);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    const near = checkIfNearBottom();
    // Hide button when user scrolls to bottom
    if (near) {
      setShowNewMessageButton(false);
    }
  }, [checkIfNearBottom]);

  // Scroll to bottom smoothly
  const scrollToBottomSmooth = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0, // With flex-col-reverse, 0 is the visual bottom
        behavior: 'smooth'
      });
      setShowNewMessageButton(false);
    }
  }, []);

  // Merge messages and hints, then sort by timestamp chronologically
  const messagesWithType = messages.map(msg => ({
    type: 'message' as const,
    timestamp: msg.timestamp,
    data: msg
  }));
  
  const hintsWithType = hints.map(hint => ({
    type: 'hint' as const,
    timestamp: hint.timestamp,
    data: hint
  }));
  
  const combinedItems = [...messagesWithType, ...hintsWithType];
  const allItems = combinedItems.sort((a, b) => a.timestamp - b.timestamp);

  // Auto-scroll to bottom when new messages arrive (only if user is already near bottom)
  // Otherwise show "new message" button
  useEffect(() => {
    const currentMessageCount = allItems.length;
    const hasNewMessages = currentMessageCount > previousMessageCountRef.current;
    
    if (hasNewMessages && previousMessageCountRef.current > 0) {
      if (isNearBottom) {
        // User is at bottom, auto-scroll
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
        }, 0);
      } else {
        // User is scrolled up, just show button
        // Note: new messages may cause slight scroll shift due to flex-col-reverse
        setShowNewMessageButton(true);
      }
    }
    
    // First message, always scroll to bottom
    if (previousMessageCountRef.current === 0 && currentMessageCount > 0) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }, 0);
    }
    
    previousMessageCountRef.current = currentMessageCount;
  }, [allItems.length, isNearBottom]);

  if (!hasContent && !isPTTMode) {
    return null;
  }

  // Bottom padding ensures content doesn't go behind Android navigation bar (~48px)
  // Keyboard mode needs more padding for the input field
  // Using env(safe-area-inset-bottom) for iOS + fixed minimum for Android
  const bottomPaddingMobile = isKeyboardMode ? '80px' : '56px'; // 56px > 48px nav bar
  
  // Suppress unused prop warning - kept for backward compatibility
  void scrollToBottom;

  return (
    <>
    {/* Mobile PTT UI: draggable button + overlays (hidden on desktop, hidden when paused)
         Architecture: the touch-handling layer is a SEPARATE div on top of the visual layer.
         This prevents Chrome from firing spurious touchcancel events when React re-renders
         the visual children (waveform, button classes, etc.) during an active touch. */}
    {isPTTMode && pttPosition && !isPaused && (
      /* PTT button container — handles touch events for drag + PTT, 
         visual children (button, waveform, gradient) are rendered inside. */
      <div
        ref={pttContainerRef}
        className={`fixed z-[60] ${
          pttPosition.isDefault
            ? `transition-[bottom,transform] duration-500 ease-in-out ${
                hasUsedPTT
                  // Desktop triangle position, Mobile stays same
                  // Above 2xl (1536px+): button stays in original position regardless of hasUsedPTT
                  ? 'bottom-[60px] md:bottom-[120px] 2xl:bottom-[200px]'
                  // Desktop floating with instruction text
                  : 'bottom-[60px] md:bottom-[200px]'
              }`
            : ''
        }`}
        style={{
          left: pttPosition.isDefault ? '50%' : `${pttPosition.x}px`,
          // When isDefault is true, we let CSS bottom rule. When dragged, we use explicit top calculation
          top: pttPosition.isDefault ? 'auto' : `${pttPosition.y}px`,
          transform: pttPosition.isDefault ? 'translateX(-50%)' : undefined,
          touchAction: 'none', // Prevent browser from interpreting touch as scroll
        }}
        onTouchStart={handlePttTouchStart}
        onTouchMove={handlePttTouchMove}
        onTouchEnd={handlePttTouchEnd}
        onTouchCancel={handlePttTouchEnd}
        onMouseDown={handlePttMouseDown}
        onMouseUp={handlePttMouseUp}
        onMouseLeave={handlePttMouseLeave}
      >
        {/* Dark radial gradient backdrop: gives contrast against the avatar background */}
        {!hidePTTGradient && (
          <div
            className="absolute inset-0 pointer-events-none -z-10 transition-opacity duration-300 md:hidden"
            style={{
              background: 'radial-gradient(circle at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 70%)',
              margin: '-2rem',
              padding: '2rem',
            }}
          />
        )}
        {/* Waveform / processing indicator — mobile only (desktop has waveform in message bubble) */}
        {isUserSpeaking && getMicrophoneFrequencyData && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none md:hidden">
            <AudioWaveform
              isActive={true}
              barCount={9}
              colorFrom="rgb(74, 222, 128)"
              colorTo="rgb(34, 197, 94)"
              getFrequencyData={getMicrophoneFrequencyData}
            />
          </div>
        )}
        
        {/* PTTButton with externalTouchControl — container handles touch events for drag + PTT */}
        <PTTButton
          onPTTPressed={onPTTPressed}
          onPTTReleased={onPTTReleased}
          isTransmitting={isUserSpeaking}
          isWaiting={isWaitingForTranscript}
          isAIResponding={!!aiResponseInProgress || isAudioPlaying}
          disabled={isAnalysisModeProcessing || !isPTTReady}
          externalTouchControl={true}
          hasUsedPTT={hasUsedPTT}
        />
      </div>
    )}

    {/* Transcript view: always visible on desktop, hidden on mobile in PTT mode */}
    <div className={`absolute top-[50%] md:top-20 bottom-0 left-0 right-0 py-2 md:py-4 z-20 pointer-events-none flex flex-col ${isPTTMode ? 'hidden md:flex' : ''}`}>
      <div className="w-full flex-1 min-h-0 flex flex-col relative">
        {/* New message button - floats above scroll container */}
        {showNewMessageButton && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center z-30 pointer-events-none">
            <button
              onClick={scrollToBottomSmooth}
              className="bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg 
                         hover:bg-blue-600 active:bg-blue-700 transition-all
                         flex items-center gap-2 text-sm font-medium pointer-events-auto
                         animate-bounce"
              aria-label="Scroll to new message"
            >
              <span>Nova mensagem</span>
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </button>
          </div>
        )}
        
        <div 
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="pointer-events-auto flex flex-col-reverse gap-2.5 md:gap-3 overflow-y-auto h-full overscroll-contain transcript-scroll-container"
          style={{ 
            // 📱 Android/iOS fix: Use max() to ensure content clears system navigation bar
            // env(safe-area-inset-bottom) for iOS notch, fixed minimum (56px) for Android nav bar
            paddingBottom: `max(env(safe-area-inset-bottom, 0px), ${bottomPaddingMobile})`
          }}
        >
          {/* === RENDERING ORDER (flex-col-reverse: first in DOM = visual bottom) ===
              Visual order top-to-bottom:
                5. Messages loop (top)
                4. Visual-only waveform - normal (no AI response)
                3. AI response in progress
                2. User interrupt bubble (during AI response) OR visual-only waveform interrupt
                1. Processing bubble (bottom)
          */}
          
          {/* 1. Analysis Mode processing indicator - visual bottom */}
          {isAnalysisModeProcessing && (
            <ProcessingBubble
              avatarImageUrl={avatarImageUrl}
              characterName={characterName}
            />
          )}
          
          {/* 2. User pending bubble - ALWAYS rendered at the bottom (most recent position).
              Any pending (empty) user message that is the current speech should appear here,
              not in the messages loop where timestamp ordering could place it above AI messages.
              This covers both interruption (during AI response) and post-cancel scenarios
              where aiResponseInProgress was already cleared but the user placeholder remains. */}
          {(() => {
            // Find any pending (empty) user message
            const pendingMsg = messages.find(msg => msg.speaker === 'user' && (!msg.text || msg.text.trim() === ''));
            
            if (pendingMsg) {
              const isCurrentSpeech = pendingMsg.id === currentTranscriptId;
              const isBeingSpoken = isUserSpeaking && isCurrentSpeech;
              const isWaiting = !isUserSpeaking && isCurrentSpeech;
              return (
                <PendingMessageBubble
                  key={`pending-${pendingMsg.id}`}
                  transcriptInProgress={isBeingSpoken ? transcriptInProgress : undefined}
                  getMicrophoneFrequencyData={isBeingSpoken ? getMicrophoneFrequencyData : undefined}
                  isUserSpeaking={isBeingSpoken}
                  isWaitingForTranscript={isWaiting || !isCurrentSpeech}
                />
              );
            }
            
            // Visual-only waveform during interruption (before placeholder is created)
            if (isUserSpeaking && !currentTranscriptId && aiResponseInProgress) {
              return (
                <PendingMessageBubble
                  key="visual-only-speaking-interrupt"
                  transcriptInProgress={transcriptInProgress}
                  getMicrophoneFrequencyData={getMicrophoneFrequencyData}
                  isUserSpeaking={true}
                  isWaitingForTranscript={false}
                />
              );
            }
            
            return null;
          })()}
          
          {/* 3. AI response in progress (typing effect) */}
          {aiResponseInProgress && (
            <AIResponseBubble
              text={aiResponseInProgress}
              avatarImageUrl={avatarImageUrl}
              characterName={characterName}
            />
          )}
          
          {/* 4. Visual-only waveform bubble - normal case (no AI response in progress).
              This covers the gap between VAD detection and SERVER_SPEECH_STARTED.
              Once the message is created (currentTranscriptId is set), we show it in the message list instead.
              Guard against ghost bubbles: don't show if there's already a pending (empty) user message. */}
          {isUserSpeaking && !currentTranscriptId && !aiResponseInProgress && !messages.some(msg => msg.speaker === 'user' && (!msg.text || msg.text.trim() === '')) && (
            <PendingMessageBubble
              key="visual-only-speaking"
              transcriptInProgress={transcriptInProgress}
              getMicrophoneFrequencyData={getMicrophoneFrequencyData}
              isUserSpeaking={true}
              isWaitingForTranscript={false}
            />
          )}
          
          {/* 5. Render messages and hints in REVERSE chronological order (because flex-col-reverse).
              During interruption, skip the pending user message (rendered separately in position 2). */}
          {allItems.slice().reverse().map((item) => {
            if (item.type === 'hint') {
              return (
                <HintBubble
                  key={item.data.id ?? `hint-${item.timestamp}`}
                  text={item.data.text}
                  type={item.data.type}
                  followed={item.data.followed}
                  autoResolved={item.data.autoResolved}
                />
              );
            } else {
              // Handle empty vs filled messages
              const isEmpty = !item.data.text || item.data.text.trim() === '';

              if (isEmpty && item.data.speaker === 'user') {
                // Empty user messages are ALWAYS rendered in position 2 (bottom),
                // never in the messages list — this prevents them from appearing
                // above AI messages due to timestamp ordering issues.
                return null;
              }

              // Regular message with text
              return (
                <MessageBubble
                  key={item.data.id ?? `msg-${item.timestamp}`}
                  speaker={item.data.speaker}
                  text={item.data.text}
                  avatarImageUrl={item.data.speaker === 'ai' ? avatarImageUrl : undefined}
                  characterName={characterName}
                  audioBlob={item.data.audioBlob}
                  onPauseMicrophone={onPauseMicrophone}
                  onResumeMicrophone={onResumeMicrophone}
                />
              );
            }
          })}
        </div>
      </div>
    </div>
    </>
  );
}

